import React, { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';

const AcceptAllConfirmModal = ({
  open,
  onClose,
  onConfirm,
  groupLabel,
  experimentCount,
  aggregationType = 'group',
  loading
}) => {
  const { t } = useTranslation();

  // Ref for dialog scroll container
  const dialogRef = useRef(null);

  // Dialog is in a portal (outside main scroll container).
  // MUI components capture wheel events for scroll-to-change behavior.
  // Solution: Intercept in capture phase, stop propagation, manually scroll page.
  useEffect(() => {
    const dialogElement = dialogRef.current;
    if (!dialogElement || !open) return;

    const handleWheel = (e) => {
      if (!dialogElement.contains(e.target)) return;

      // CRITICAL: Stop event from reaching MUI components that would block scroll
      e.stopPropagation();

      // Manually scroll the main page content
      const pageScrollContainer = document.querySelector('.css-4z6nar') || document.documentElement;
      pageScrollContainer.scrollTop += e.deltaY;
    };

    // Use capture phase to intercept before MUI's handlers
    dialogElement.addEventListener('wheel', handleWheel, { capture: true, passive: false });

    return () => {
      dialogElement.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, [open]);

  // Get aggregation-specific labels
  const getAggregationLabel = () => {
    switch (aggregationType) {
      case 'route':
        return t('route', 'Route');
      case 'sector':
        return t('sector', 'Sector');
      case 'group':
      default:
        return t('group', 'Group');
    }
  };

  const getConfirmationMessage = () => {
    switch (aggregationType) {
      case 'route':
        return t('acceptAllConfirmMessageRoute', 'Are you sure you want to accept all experiments in this route?');
      case 'sector':
        return t('acceptAllConfirmMessageSector', 'Are you sure you want to accept all experiments in this sector?');
      case 'group':
      default:
        return t('acceptAllConfirmMessageGroup', 'Are you sure you want to accept all experiments in this group?');
    }
  };

  const aggregationLabel = getAggregationLabel();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="accept-all-dialog-title"
      aria-describedby="accept-all-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        ref: dialogRef
      }}
    >
      <DialogTitle id="accept-all-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">
            {t('confirmAcceptAll', 'Confirm Accept All')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="accept-all-dialog-description">
          <Box component="div">
            <Typography variant="body1" gutterBottom>
              {getConfirmationMessage()}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>{t('summary', 'Summary')}:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • {t('totalExperimentsToAccept', '{{count}} experiment(s) will be accepted', { count: experimentCount })}
            </Typography>
            <Typography variant="body2">
              • {aggregationLabel}: <strong>{groupLabel}</strong>
            </Typography>
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="primary"
          autoFocus
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>{t('accepting', 'Accepting...')}</span>
            </Box>
          ) : (
            t('confirmAcceptAll', 'Confirm Accept All')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcceptAllConfirmModal;